import React, { useEffect, createElement } from 'react';
interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}
function setMetaTag(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
function setJsonLd(data: Record<string, unknown>) {
  const id = 'seo-json-ld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
function removeJsonLd() {
  const el = document.getElementById('seo-json-ld');
  if (el) el.remove();
}
const BASE_URL = 'https://viridiaanalytics.com';
export function SEOHead({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage = `${BASE_URL}/og-image.png`,
  noindex = false,
  jsonLd
}: SEOHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;
    // Core meta
    setMetaTag('description', description);
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    // Canonical — always strip query parameters
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : `${BASE_URL}${window.location.pathname}`;
    setCanonical(canonicalUrl);
    // Open Graph
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:site_name', 'Viridia Analytics', true);
    setMetaTag('og:locale', 'en_US', true);
    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    // Geo tags for local SEO
    setMetaTag('geo.region', 'US-AZ');
    setMetaTag('geo.placename', 'Phoenix');
    // JSON-LD
    if (jsonLd) {
      setJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }
    return () => {
      // Cleanup JSON-LD on unmount
      removeJsonLd();
    };
  }, [title, description, canonical, keywords, ogType, ogImage, noindex, jsonLd]);
  return null;
}
// Pre-built JSON-LD for the business
export const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Viridia Analytics',
  description: 'Phoenix marketing agency specializing in ad campaigns, automations, and web design for local service businesses.',
  url: BASE_URL,
  telephone: '+16233000708',
  email: 'info@viridiaanalytics.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Phoenix',
    addressRegion: 'AZ',
    addressCountry: 'US'
  },
  areaServed: {
    '@type': 'City',
    name: 'Phoenix',
    sameAs: 'https://en.wikipedia.org/wiki/Phoenix,_Arizona'
  },
  priceRange: '$$',
  sameAs: ['https://www.facebook.com/profile.php?id=61571074363261', 'https://www.instagram.com/viridiaanalyticsllc/', 'https://www.linkedin.com/company/viridia-analytics/', 'https://www.yelp.com/biz/viridia-analytics-phoenix-2'],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Marketing Services',
    itemListElement: [{
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Ad Campaign Management',
        description: 'Google Ads, Meta Ads, and Yelp Ads management for local businesses'
      }
    }, {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Marketing Automations',
        description: 'CRM integration, automated follow-up, and lead nurturing systems'
      }
    }, {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Web Design & SEO',
        description: 'Conversion-focused websites with local SEO optimization'
      }
    }]
  }
};